import type { Tone } from '@coachrex/shared';

/** "9:31", "17:44": 24-hour without a leading zero, like the design. */
function clock(d: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hourCycle: 'h23', timeZone }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '0';
  return `${Number(get('hour'))}:${get('minute')}`;
}

/** "Fri, Oct 9 · 9:31" in the workspace time zone. */
export function stamp(iso: string, timeZone: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone });
  return `${date} · ${clock(d, timeZone)}`;
}

/** "Today, 10:02" / "Yesterday, 9:40" / "Oct 3, 14:10" for notes. */
export function relativeStamp(iso: string, timeZone: string, now = new Date()): string {
  const d = new Date(iso);
  const day = (x: Date) => x.toLocaleDateString('en-CA', { timeZone });
  const time = clock(d, timeZone);
  if (day(d) === day(now)) return `Today, ${time}`;
  if (day(d) === day(new Date(now.getTime() - 86_400_000))) return `Yesterday, ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone })}, ${time}`;
}

export const toneClass = (t: Tone) => `tone-${t}`;

export function scoreColor(score: number): string {
  return score >= 80 ? 'var(--score-good)' : score >= 65 ? 'var(--score-ok)' : 'var(--score-bad)';
}

export function engagementTone(label: 'High' | 'Medium' | 'Low'): Tone {
  return label === 'High' ? 'green' : label === 'Medium' ? 'amber' : 'grey';
}

export const toneFg = (t: Tone) => `var(--${t}-fg)`;

export function firstName(name: string): string {
  return name.split(' ')[0] ?? name;
}

/** "$12,400" in the workspace currency, no cents. */
export function money(value: number | null | undefined, currency: string): string {
  if (value == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${currency} ${Math.round(value).toLocaleString('en-US')}`;
  }
}
